import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { Application } from './application';
import { PROFILE_ROLE } from '../core/auth/profile-role';
import { PATH } from '../core/const/path';
import { Context } from '../core/auth/context';
import { ContextGroup } from './context-group';
import { ENTITY_TYPE } from '../core/const/entity-type';

@Injectable({
  providedIn: 'root'
})
export class NavbarService {

  private readonly httpClient = inject(HttpClient);

  getApplicationsVersion2(): Observable<Application[]> {
    return this.httpClient.get<Application[]>('/v2/me/applications');
  }

  getContextGroups(list: Context[], msg: { [key: string]: string }): ContextGroup[] {
    const contextGroups: ContextGroup[] = [];

    const customerGroup = this.getCustomerContextGroup(list, msg);
    if (customerGroup) contextGroups.push(customerGroup);

    const companyGroup = this.getCompanyContextGroup(list, msg);
    if (companyGroup) contextGroups.push(companyGroup);

    const distributorGroup = this.getDistributorContextGroup(list, msg);
    if (distributorGroup) contextGroups.push(distributorGroup);

    return contextGroups;
  }

  private getCustomerContextGroup(list: Context[], msg: { [key: string]: string }): ContextGroup | null {
    const items = list
      .filter(context => context.roles.includes(PROFILE_ROLE.subscriberAdministrator) || context.roles.includes(PROFILE_ROLE.administrator))
      .sort((a, b) => a.name.localeCompare(b.name));
    return items.length > 0 ? new ContextGroup(msg['labelSubscriber'], ENTITY_TYPE.customer, items) : null;
  }

  private getCompanyContextGroup(list: Context[], msg: { [key: string]: string }): ContextGroup | null {
    const items = list
      .filter(context => context.roles.includes(PROFILE_ROLE.companyAdministrator))
      .sort((a, b) => a.name.localeCompare(b.name));
    return items.length > 0 ? new ContextGroup(msg['labelCompany'], ENTITY_TYPE.company, items) : null;
  }

  private getDistributorContextGroup(list: Context[], msg: { [key: string]: string }): ContextGroup | null {
    const seen = new Set<string>();
    const items = list
      .filter(context => context.roles.includes(PROFILE_ROLE.buAccessAdministrator) || context.roles.includes(PROFILE_ROLE.buSubscriberViewer))
      .filter(context => {
        if (seen.has(context.contextId)) return false;
        seen.add(context.contextId);
        return true;
      })
      .sort((a, b) => a.name.localeCompare(b.name));
    return items.length > 0 ? new ContextGroup(msg['labelBusinessUnit'], ENTITY_TYPE.distributor, items) : null;
  }

  getRootContextPath(roles: string[], taskId?: string): string {
    if (roles.includes(PROFILE_ROLE.buAccessAdministrator) || roles.includes(PROFILE_ROLE.buSubscriberViewer)) {
      return PATH.context_distributor;
    } else if (roles.includes(PROFILE_ROLE.subscriberAdministrator)) {
      return PATH.context_customer;
    } else if (roles.includes(PROFILE_ROLE.companyAdministrator)) {
      return PATH.context_company;
    } else if (roles.includes(PROFILE_ROLE.administrator)) {
      // An Administrator context with a pending task goes to onboarding; otherwise to provisioning.
      return taskId ? PATH.set_up_your_company : PATH.provisioning;
    }
    return '/forbidden';
  }

}
